import * as React from 'react'
import DatePicker from 'react-datepicker'
import Select from 'react-select'
import {DrawSource} from '../../enums/DrawSource'
import {DrawType} from '../../enums/DrawType'
import {GeometryType} from '../../enums/GeometryType'
import {IFeatureLayerInfo} from '../../interfaces/models'
import {
  ControlType,
  IControlQueries,
  IControlQuery,
  IFieldInfo,
  IGeometryQuery,
  IRawQueries,
  QueryTarget
} from '../../interfaces/reducers'
import {Feature} from '../../models'
import {getMapService} from '../../reducers/map'
import {FloatingLabelContainer} from '../Controls/FloatingLabelContainer'

export interface State {
  controledQueries: IControlQueries
  queryTarget: QueryTarget
  currentlyFocused: string
  geometryQuery?: IGeometryQuery
  drawMessage: string
}

export interface Props {
  expandable: boolean
  layerInfo: IFeatureLayerInfo
  defaultExpanded?: boolean
  defaultControlQueries: IControlQueries
  geometryQuery?: IGeometryQuery
  setQueries: (
    key: string,
    controlQueries?: IControlQueries,
    rawQueries?: IRawQueries,
    geometryQuery?: IGeometryQuery,
    updateClients?: boolean
  ) => void
}

export class DataFilters extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      controledQueries: props.defaultControlQueries,
      queryTarget: QueryTarget.Data,
      currentlyFocused: '',
      geometryQuery: props.geometryQuery,
      drawMessage: 'Задайте територия на търсене'
    }
    this.forceUpdate = this.forceUpdate.bind(this)
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.defaultControlQueries !== this.props.defaultControlQueries ||
      prevProps.geometryQuery !== this.props.geometryQuery
    ) {
      this.setState({
        ...this.state,
        controledQueries: this.props.defaultControlQueries,
        geometryQuery: this.props.geometryQuery
      })
    }
  }

  protected changeTarget(queryTarget: QueryTarget) {
    const newControlQueries: IControlQueries = {}
    for (const key in this.state.controledQueries) {
      newControlQueries[key] = {...this.state.controledQueries[key], queryTarget}
    }
    this.setState({...this.state, controledQueries: {...newControlQueries}})
  }

  protected onFieldValueChange(fieldName: string, controlType: ControlType, controlValue: any, where: string) {
    const newControlQueries: IControlQueries = {}
    newControlQueries[fieldName] = {controlType, controlValue, where, queryTarget: this.state.queryTarget}
    this.setState({...this.state, controledQueries: {...this.state.controledQueries, ...newControlQueries}})
  }
  protected getMultiSelectEditingControl(
    currentValue: any,
    fieldName: string,
    fieldInfo: IFieldInfo,
    readonly?: boolean
  ) {
    const options: {value: any; label: any}[] = []
    fieldInfo.codedValues!.forEach(c => {
      options.push({value: c.code, label: c.name})
    })
    return (
      <Select
        isDisabled={!!readonly}
        className="w"
        isMulti={true}
        closeMenuOnSelect={false}
        value={currentValue}
        placeholder={`Изберете ${fieldInfo.label}...`}
        onChange={(newValues: {value: any; label: any}[]) => {
          const sqlArray: string[] = []
          if (newValues) {
            newValues.forEach(v => {
              sqlArray.push(`"${fieldName}" = ${v.value}`)
            })
          }

          this.onFieldValueChange(
            fieldName,
            ControlType.MultiSelect,
            newValues,
            sqlArray.length > 0 ? '(' + sqlArray.join(' OR ') + ')' : ''
          )
        }}
        options={options}
      />
    )
  }
  protected getEnumMultiSelectEditingControl(
    currentValue: any,
    fieldName: string,
    fieldInfo: IFieldInfo,
    readonly?: boolean
  ) {
    const options: {value: any; label: any}[] = []
    const selected: {value: any; label: any}[] = []
    fieldInfo.codedValues!.forEach(c => {
      options.push({value: c.code, label: c.name})
      if (!!currentValue && (currentValue as number) | (c.code as number)) {
        selected.push({value: c.code, label: c.name})
      }
    })
    return (
      <Select
        isDisabled={!!readonly}
        className="w"
        isMulti={true}
        closeMenuOnSelect={false}
        value={selected}
        placeholder={`Изберете ${fieldInfo.label}...`}
        onChange={(newValues: {value: any; label: any}[]) => {
          let selectedInt = 0
          if (newValues) {
            newValues.forEach(v => {
              selectedInt = selectedInt | v.value
            })
          }
          let where = ''
          if (selectedInt) {
            where = `"${fieldName}" | ${selectedInt}`
          }
          this.onFieldValueChange(fieldName, ControlType.MultiSelect, selectedInt, where)
        }}
        options={options}
      />
    )
  }

  protected getStringEditingControl(
    controlValue: React.ReactText | undefined,
    fieldName: string,
    fieldInfo: IFieldInfo
  ) {
    const onFieldValueChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      this.onFieldValueChange(
        fieldName,
        ControlType.StringInput,
        e.target.value,
        e.target.value ? `upper("${fieldName}") LIKE '%${e.target.value.toUpperCase().replace(' ', '%')}%'` : ''
      )
    }

    return (
      <input
        onFocus={() => this.setState({...this.state, currentlyFocused: fieldName})}
        className="form-control"
        type="text"
        value={controlValue === undefined || controlValue === null ? '' : controlValue}
        onChange={onFieldValueChange}
      ></input>
    )
  }

  protected getNumberEditingControl(
    controlValue: {from?: number; to?: number},
    fieldName: string,
    fieldInfo: IFieldInfo,
    from: boolean
  ) {
    let val: number | undefined = undefined
    if (controlValue) {
      val = from ? controlValue.from : controlValue.to
    }
    const whereFromValue = (vals: {from?: number; to?: number}): string => {
      let where = ''
      if (vals.from != undefined) {
        where += `"${fieldName}" >= ${vals.from}`
        if (vals.to != undefined) {
          where += ' AND '
        }
      }
      if (vals.to != undefined) {
        where += `"${fieldName}" <= ${vals.to}`
      }
      return where
    }
    return (
      <input
        onFocus={() => this.setState({...this.state, currentlyFocused: fieldName})}
        className="form-control"
        type="number"
        pattern="\d*"
        value={val === undefined || val === null ? '' : val}
        onChange={e => {
          let value = e.target.value as any
          if (value) {
            if (fieldInfo.type == 'integer' || fieldInfo.type == 'long') {
              value = parseInt(value)
            } else {
              value = parseFloat(value)
            }
            if (from) {
              controlValue.from = value
            } else {
              controlValue.to = value
            }
            const where = whereFromValue(controlValue)
            this.onFieldValueChange(fieldName, ControlType.NumberInput, controlValue, where)
          } else {
            if (from) {
              controlValue.from = undefined
            } else {
              controlValue.to = undefined
            }
            const where = whereFromValue(controlValue)
            this.onFieldValueChange(fieldName, ControlType.NumberInput, controlValue, where)
          }
        }}
      ></input>
    )
  }

  getChangedDate(changedValue: any, fieldInfo: IFieldInfo): Date | undefined {
    if (changedValue) {
      const newDate = new Date(changedValue)
      if (fieldInfo.forceTime) {
        fieldInfo.forceTime.forEach((v, i) => {
          if (v == undefined) {
            return
          }
          if (i == 0) {
            newDate.setHours(v)
          } else if (i == 1) {
            newDate.setMinutes(v)
          } else if (i == 2) {
            newDate.setSeconds(v)
          } else if (i == 3) {
            newDate.setMilliseconds(v)
          }
        })
      }
      if (fieldInfo.forceDate) {
        fieldInfo.forceDate.forEach((v, i) => {
          if (v == undefined) {
            return
          }
          if (i == 0) {
            newDate.setFullYear(v)
          } else if (i == 1) {
            newDate.setMonth(v - 1)
          } else if (i == 2) {
            newDate.setDate(v)
          }
        })
      }
      return newDate
    } else {
      return undefined
    }
  }
  protected getLocalISOTime(twDate: Date, fieldInfo: IFieldInfo): string {
    if (fieldInfo.dateSupportsOffset) {
      const localOffset = twDate.getTimezoneOffset() * 60000
      const newdate = new Date(twDate.getTime() - localOffset)
      return newdate.toISOString().replace('.000', '')
    }
    return twDate.toISOString()
    // obtain local UTC offset and convert to msec
  }
  protected getDateEditingDatePicker(
    dateFormat: string,
    fieldName: string,
    fieldInfo: IFieldInfo,
    controlValue: {start?: Date; end?: Date},
    type: 'start' | 'end'
  ): JSX.Element {
    const getWhere = (controlValue: {start?: Date; end?: Date}): string => {
      const wheres: string[] = []
      if (controlValue.start) {
        wheres.push(`"${fieldInfo.fieldName}" >= timestamp '${this.getLocalISOTime(controlValue.start, fieldInfo)}'`)
      }
      if (controlValue.end) {
        wheres.push(`"${fieldInfo.fieldName}" <= timestamp '${this.getLocalISOTime(controlValue.end, fieldInfo)}'`)
      }
      return wheres.join(' AND ')
    }
    return (
      <DatePicker
        onFocus={() => this.setState({...this.state, currentlyFocused: fieldName})}
        showTimeSelect={dateFormat.indexOf('HH:mm') != -1}
        showTimeSelectOnly={dateFormat == 'HH:mm'}
        timeFormat="HH:mm"
        timeIntervals={15}
        dateFormat={dateFormat}
        className="form-control"
        selected={type == 'end' ? controlValue.end : controlValue.start}
        minDate={type == 'end' ? controlValue.start : undefined}
        maxDate={type == 'start' ? controlValue.end : undefined}
        onChange={v => {
          let forceTime = [0, 0, 0, 0]
          if (type == 'end') {
            forceTime = [23, 59, 59, 0]
          }
          const newDate = this.getChangedDate(v, {...fieldInfo, forceTime})
          if (type == 'start') {
            controlValue.start = newDate
          } else {
            controlValue.end = newDate
          }
          this.onFieldValueChange(fieldInfo.fieldName, ControlType.TimeSpan, controlValue, getWhere(controlValue))
        }}
        isClearable={fieldInfo.nullable}
        showYearDropdown
      />
    )
  }

  protected useAsFilter(fieldInfo: IFieldInfo): boolean {
    return fieldInfo.visible
  }

  protected getControlQuery(fieldInfo: IFieldInfo, controlQuery?: IControlQuery): IControlQuery {
    let controlType: ControlType
    let controlValue = undefined
    let readonly = false
    if (controlQuery) {
      controlType = controlQuery.controlType
      controlValue = controlQuery.controlValue
      readonly = !!controlQuery.readonly
    } else {
      if (fieldInfo.codedValues) {
        controlType = ControlType.MultiSelect
        controlValue = []
      } else if (fieldInfo.type == 'date') {
        controlType = ControlType.TimeSpan
        controlValue = {}
      } else if (fieldInfo.type == 'string') {
        controlType = ControlType.StringInput
      } else if (
        fieldInfo.type == 'integer' ||
        fieldInfo.type == 'small-integer' ||
        fieldInfo.type == 'long' ||
        fieldInfo.type == 'double'
      ) {
        controlType = ControlType.NumberInput
        controlValue = {
          from: undefined,
          to: undefined
        }
      } else if (fieldInfo.type == 'enum') {
        controlType = ControlType.EnumSelect
        controlValue = undefined
      } else {
        throw new Error('Unsupported format ' + fieldInfo.type)
      }
    }
    const controlQueryResult: IControlQuery = {
      controlType: controlType,
      controlValue: controlValue,
      queryTarget: this.state.queryTarget,
      where: controlQuery ? controlQuery.where : '',
      readonly: readonly
    }

    return controlQueryResult
  }

  protected getInitControls(): JSX.Element[] {
    return []
  }

  protected renderControls(): JSX.Element[] {
    const result: JSX.Element[] = this.getInitControls()
    for (const fieldName in this.props.layerInfo.fields) {
      const fieldInfo = this.props.layerInfo.fields[fieldName]
      if (this.useAsFilter(fieldInfo)) {
        const controlQuery = this.getControlQuery(fieldInfo, this.state.controledQueries[fieldName])
        if (controlQuery.controlType == ControlType.MultiSelect) {
          result.push(
            <div className="data-filter-row" key={fieldName}>
              {this.getMultiSelectEditingControl(
                controlQuery.controlValue,
                fieldName,
                fieldInfo,
                controlQuery.readonly
              )}
            </div>
          )
        }
        if (controlQuery.controlType == ControlType.EnumSelect) {
          result.push(
            <div className="data-filter-row" key={fieldName}>
              {this.getEnumMultiSelectEditingControl(
                controlQuery.controlValue,
                fieldName,
                fieldInfo,
                controlQuery.readonly
              )}
            </div>
          )
        } else if (controlQuery.controlType == ControlType.TimeSpan) {
          let dateFormat = 'dd.MM.yyyy'
          if (fieldInfo.dateFormat) {
            dateFormat = fieldInfo.dateFormat
          }
          const fieldName1 = fieldName + '1'
          const fieldName2 = fieldName + '2'
          result.push(
            <FloatingLabelContainer
              className="data-filter-row"
              key={fieldName1}
              focused={!!controlQuery.controlValue.start || this.state.currentlyFocused == fieldName1}
              label={`"${fieldInfo.label}"` + ' от дата'}
            >
              {this.getDateEditingDatePicker(dateFormat, fieldName1, fieldInfo, controlQuery.controlValue, 'start')}
            </FloatingLabelContainer>
          )
          result.push(
            <FloatingLabelContainer
              className="data-filter-row"
              key={fieldName2}
              focused={!!controlQuery.controlValue.end || this.state.currentlyFocused == fieldName2}
              label={`"${fieldInfo.label}"` + ' до дата'}
            >
              {this.getDateEditingDatePicker(dateFormat, fieldName2, fieldInfo, controlQuery.controlValue, 'end')}
            </FloatingLabelContainer>
          )
        } else if (controlQuery.controlType == ControlType.StringInput) {
          result.push(
            <FloatingLabelContainer
              className="data-filter-row"
              key={fieldName}
              focused={!!controlQuery.controlValue || this.state.currentlyFocused == fieldName}
              label={fieldInfo.label}
            >
              {this.getStringEditingControl(controlQuery.controlValue, fieldName, fieldInfo)}
            </FloatingLabelContainer>
          )
        } else {
          result.push(
            <FloatingLabelContainer
              className="data-filter-row"
              key={fieldName + '#1'}
              focused={
                (controlQuery.controlValue &&
                  controlQuery.controlValue.from !== '' &&
                  controlQuery.controlValue.from !== undefined &&
                  controlQuery.controlValue.from !== null) ||
                this.state.currentlyFocused == fieldName + '#1'
              }
              label={fieldInfo.label + ' от'}
            >
              {this.getNumberEditingControl(controlQuery.controlValue, fieldName, fieldInfo, true)}
            </FloatingLabelContainer>
          )
          result.push(
            <FloatingLabelContainer
              className="data-filter-row"
              key={fieldName + '#2'}
              focused={
                (controlQuery.controlValue &&
                  controlQuery.controlValue.to !== '' &&
                  controlQuery.controlValue.to !== undefined &&
                  controlQuery.controlValue.to !== null) ||
                this.state.currentlyFocused == fieldName + '#2'
              }
              label={fieldInfo.label + ' до'}
            >
              {this.getNumberEditingControl(controlQuery.controlValue, fieldName, fieldInfo, false)}
            </FloatingLabelContainer>
          )
        }
      }
    }
    return result
  }

  onDeactivated() {
    getMapService().drawing.unMark(DrawSource.Select)
  }

  callBackOnComplete = (feature: Feature) => {
    this.setState({
      ...this.state,
      geometryQuery: {
        geometry: feature.getGeometry(),
        queryTarget: QueryTarget.Data
      }
    })
  }

  getGeometrySelectorButton(): JSX.Element {
    let feature: Feature
    if (this.props.geometryQuery) {
      feature = new Feature()
      feature.setGeometry(this.props.geometryQuery.geometry)
    }
    return (
      <div className="geometry-filter-selector">
        <button
          type="button"
          onClick={() => {
            this.setState({...this.state, drawMessage: 'Изчертайте правоъгълник на картата'})
            getMapService().drawing.unMark(DrawSource.Select, [feature])
            getMapService().drawing.activateDraw(
              DrawType.Rectangle,
              DrawSource.Select,
              this.onDeactivated,
              this.callBackOnComplete
            )
          }}
          className="button margin"
        >
          <img src="resources/icons/selectRectangle.svg" style={{height: '28px', width: '28px'}} alt="" />
        </button>
        <div>{this.state.geometryQuery ? 'Зададен е правоъгълник' : this.state.drawMessage}</div>
        {this.state.geometryQuery ? (
          <img
            src="resources/icons/remove.svg"
            style={{height: '24px', width: '24px', cursor: 'pointer'}}
            alt=""
            onClick={() => {
              this.setState({...this.state, drawMessage: 'Задайте територия на търсене', geometryQuery: undefined})
            }}
          />
        ) : null}
      </div>
    )
  }

  render() {
    return (
      <div className="data-filters">
        <div className="actions">
          <button
            type="button"
            className="button"
            onClick={() => {
              this.props.setQueries(this.props.layerInfo.url, this.state.controledQueries, {}, this.state.geometryQuery)
            }}
          >
            Филтрирай
          </button>

          <button
            type="button"
            className="button"
            onClick={() => {
              this.setState({...this.setState, controledQueries: {}, currentlyFocused: '', geometryQuery: undefined})
              this.props.setQueries(this.props.layerInfo.url, undefined, {})
            }}
          >
            Изчисти филтрите
          </button>
        </div>

        <div className={`data-filters-expander ${this.props.expandable ? 'expandable' : ''}`}>
          {this.renderControls()}
          {this.props.layerInfo.getGeometryType() != GeometryType.None && this.getGeometrySelectorButton()}
        </div>
      </div>
    )
  }
}
